import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { verificationAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CHIRELogo, CHIREWordmark } from '../components/CHIRELogo';

const DOC_STATUS = {
  approved: { color: '#059669', bg: 'rgba(16,185,129,0.08)', label: 'Approved' },
  rejected: { color: '#DC2626', bg: 'rgba(239,68,68,0.08)', label: 'Rejected' },
  needs_review: { color: '#D97706', bg: 'rgba(245,158,11,0.08)', label: 'Under Review' },
  pending: { color: '#0076A3', bg: 'rgba(30,104,255,0.08)', label: 'Processing' },
};

export default function CandidateVerificationPage() {
  const { accessCode } = useParams();
  const [data, setData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [idNumbers, setIdNumbers] = useState({});
  const fileInputRefs = useRef({});

  const loadData = useCallback(async () => {
    try {
      const res = await verificationAPI.getByCode(accessCode);
      setData(res.data);
      setDocuments(res.data.documents || []);
    } catch (err) {
      toast.error('Verification request not found');
    } finally { setLoading(false); }
  }, [accessCode]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh while docs pending
  useEffect(() => {
    const hasPending = documents.some(d => d.status === 'pending');
    if (!hasPending) return;
    const iv = setInterval(loadData, 3000);
    return () => clearInterval(iv);
  }, [documents, loadData]);

  const handleUpload = async (documentType, file) => {
    if (!accessCode) return;
    setUploading(prev => ({ ...prev, [documentType]: true }));
    try {
      await verificationAPI.uploadDocument(accessCode, file, documentType);
      toast.success(`${documentType} uploaded! AI is validating...`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [documentType]: false }));
    }
  };

  if (loading) return <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F1EAE1',color:'#64748B' }}>Loading...</div>;
  if (!data) return <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F1EAE1',color:'#64748B' }}>Not found</div>;

  const requirements = data.requirements || [];
  const approved = documents.filter(d => d.status === 'approved').length;
  const mandatoryCount = requirements.filter(r => r.is_mandatory).length;

  return (
    <div style={{ minHeight: '100vh', background: '#F1EAE1', padding: '2rem' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.625rem', marginBottom: '1rem' }}>
            <img src="/chire-logo-full.png" alt="" style={{ height: 36, objectFit: 'contain' }} />
          </div>
          <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: '#002A38', marginBottom: '0.25rem' }}>Document Verification</h1>
          <p style={{ fontSize: '0.875rem', color: '#64748B' }}>{data.job_title} &middot; {data.candidate_name}</p>
        </div>

        {/* Progress */}
        <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem', marginBottom: '1.5rem', textAlign: 'center' }}>
          <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '2rem', fontWeight: 700, color: approved >= mandatoryCount ? '#059669' : '#0076A3' }}>
            {approved}/{mandatoryCount}
          </div>
          <div style={{ fontSize: '0.8125rem', color: '#64748B' }}>mandatory documents approved</div>
          <div style={{ height: 6, borderRadius: 3, background: '#ECE7DE', marginTop: '0.75rem', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 3, background: approved >= mandatoryCount ? '#10B981' : '#0076A3', width: `${mandatoryCount > 0 ? (approved/mandatoryCount)*100 : 0}%`, transition: 'width 0.6s' }} />
          </div>
        </Card>

        {/* Document list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {requirements.map((req, i) => {
            const uploaded = documents.find(d => d.document_type === req.document_type);
            const statusCfg = uploaded ? (DOC_STATUS[uploaded.status] || DOC_STATUS.pending) : null;
            const isUploading = uploading[req.document_type];

            return (
              <Card key={`req-${i}`} style={{ background: '#fff', border: `1px solid ${uploaded?.status === 'approved' ? 'rgba(16,185,129,0.3)' : '#ECE7DE'}`, borderRadius: 14, padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <div>
                    <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#002A38' }}>
                      {req.document_type}
                      {req.is_mandatory && <span style={{ color: '#DC2626', marginLeft: 4, fontSize: '0.75rem' }}>Required</span>}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#94A3B8' }}>{req.description}</div>
                  </div>
                  {uploaded && (
                    <Badge style={{ background: statusCfg.bg, color: statusCfg.color, borderRadius: 999, fontSize: '0.6875rem' }}>{statusCfg.label}</Badge>
                  )}
                </div>

                {uploaded ? (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748B', marginBottom: '0.25rem' }}>
                      Uploaded: {uploaded.filename}
                      {uploaded.expiry_date && ` &middot; Expires: ${uploaded.expiry_date}`}
                    </div>
                    {uploaded.validation_result?.summary && (
                      <div style={{ fontSize: '0.75rem', color: '#64748B', fontStyle: 'italic' }}>{uploaded.validation_result.summary}</div>
                    )}
                    {uploaded.status === 'rejected' && uploaded.rejection_reason && (
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 8, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', fontSize: '0.75rem', color: '#DC2626' }}>
                        Rejected: {uploaded.rejection_reason}
                      </div>
                    )}
                    {(uploaded.status === 'rejected') && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <input type="file" ref={el => fileInputRefs.current[req.document_type] = el} style={{ display: 'none' }}
                          accept=".pdf,.docx,.txt,.jpg,.jpeg,.png" onChange={e => { if (e.target.files[0]) handleUpload(req.document_type, e.target.files[0]); }} />
                        <Button onClick={() => fileInputRefs.current[req.document_type]?.click()} variant="outline" style={{ borderRadius: 8, fontSize: '0.75rem' }}>
                          Re-upload
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    {req.requires_id_number && (
                      <div style={{ marginBottom: '0.625rem' }}>
                        <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: '#64748B', marginBottom: '0.25rem' }}>
                          {req.id_label || 'ID Number'} <span style={{ color: '#DC2626' }}>*</span>
                        </label>
                        <input
                          value={idNumbers[req.document_type] || ''}
                          onChange={e => setIdNumbers(prev => ({ ...prev, [req.document_type]: e.target.value }))}
                          placeholder={req.id_mask || 'Enter ID number'}
                          style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #CBD5E1', background: '#FAF7F2', color: '#002A38', fontSize: '0.8125rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}
                        />
                      </div>
                    )}
                    <input type="file" ref={el => fileInputRefs.current[req.document_type] = el} style={{ display: 'none' }}
                      accept=".pdf,.docx,.txt,.jpg,.jpeg,.png" onChange={e => { if (e.target.files[0]) handleUpload(req.document_type, e.target.files[0]); }} />
                    <Button
                      onClick={() => {
                        if (req.requires_id_number && !idNumbers[req.document_type]?.trim()) {
                          toast.error(`Please enter your ${req.id_label || 'ID number'} before uploading`);
                          return;
                        }
                        fileInputRefs.current[req.document_type]?.click();
                      }}
                      disabled={isUploading}
                      style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.8125rem', fontWeight: 600 }}>
                      {isUploading ? 'Uploading...' : 'Upload Document'}
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
